import Head from "next/head";
import { generateNextSeo } from "next-seo/pages";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Separator } from "@/components/ui/separator";
import { useRouter } from "next/router";
import useCurrentAccount from "@/components/context/useCurrentAccount";
import Link from "next/link";
import {
  ShieldCheckIcon,
  LinkIcon,
  ClockIcon,
  GlobeAmericasIcon,
  CheckIcon,
  ArrowRightIcon,
  DocumentCheckIcon,
  BellIcon,
  CommandLineIcon,
  DevicePhoneMobileIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
} from "@heroicons/react/24/outline";

export default function LandingPage() {
  const router = useRouter();
  const { account } = useCurrentAccount();

  return (
    <div className="min-h-screen bg-background">
      <Head>
        {generateNextSeo({
          title: "Uptime Monitoring That Detects Downtime in 10 Seconds | Uptime Monitor",
          description:
            "Website and server uptime monitoring with 10-second checks from AWS regions on 6 continents. Native PagerDuty, incident.io, Slack alerts. Start with a 7-day free trial.",
          canonical: "https://uptime-monitor.io/",
          openGraph: {
            title: "Uptime Monitoring That Detects Downtime in 10 Seconds",
            description:
              "Website and server uptime monitoring with 10-second checks from AWS regions on 6 continents. Native PagerDuty, incident.io, and Slack integration.",
            url: "https://uptime-monitor.io/",
          },
        })}
      </Head>

      <Header />

      <main>
        {/* Business Hero */}
        <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-muted/50 to-background">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-3xl">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                Uptime Monitoring
                <span className="text-primary"> Done Right</span>
              </h1>

              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                10-second checks for your websites and servers from AWS regions on 6 continents.
                Multi-region consensus means far fewer false positives. Instant alerts via PagerDuty,
                incident.io, Slack, and more.
              </p>

              <div className="flex flex-wrap gap-6 mb-10">
                <div className="flex items-center gap-2">
                  <ClockIcon className="w-5 h-5 text-primary" />
                  <span className="font-medium">10-second detection</span>
                </div>
                <div className="flex items-center gap-2">
                  <GlobeAmericasIcon className="w-5 h-5 text-primary" />
                  <span className="font-medium">Global coverage</span>
                </div>
                <div className="flex items-center gap-2">
                  <LinkIcon className="w-5 h-5 text-primary" />
                  <span className="font-medium">Native integrations</span>
                </div>
              </div>

              {account ? (
                <div className="flex gap-4">
                  <Button size="lg" onClick={() => router.push("/dashboard/monitors")}>
                    Dashboard
                    <ArrowRightIcon className="w-4 h-4 ml-2" />
                  </Button>
                  <Link href="/docs">
                    <Button size="lg" variant="outline">
                      Documentation
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="flex gap-4">
                  <Link href="/accounts/login">
                    <Button size="lg" className="cursor-pointer">
                      Setup Monitoring
                    </Button>
                  </Link>
                  <Link href="/docs">
                    <Button size="lg" variant="outline" className="cursor-pointer">
                      Documentation
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Trust Indicators */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 border-y">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-3xl font-bold">2B+</div>
                <div className="text-sm text-muted-foreground">Checks Per Month</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold">3x</div>
                <div className="text-sm text-muted-foreground">Faster Than Others</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold">6</div>
                <div className="text-sm text-muted-foreground">Continents Covered</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold">7-Day</div>
                <div className="text-sm text-muted-foreground">Free Trial</div>
              </div>
            </div>
          </div>
        </section>

        {/* AI Skill Block */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <Badge variant="outline" className="mb-6">
                  <CommandLineIcon className="w-3 h-3 mr-1" />
                  AI-Powered Setup
                </Badge>
                <h2 className="text-3xl font-bold mb-4">
                  Set Up Monitoring From Your
                  <span className="text-primary"> AI Assistant</span>
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Tell Claude what to monitor. It handles installation, authentication, and
                  configuration — all without leaving your editor. Get alerts on your iPhone, Slack,
                  email, or PagerDuty.
                </p>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="flex items-center gap-2">
                    <ChatBubbleLeftRightIcon className="w-5 h-5 text-primary" />
                    <span className="text-sm font-medium">Slack & Discord</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <DevicePhoneMobileIcon className="w-5 h-5 text-primary" />
                    <span className="text-sm font-medium">iPhone Push</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <EnvelopeIcon className="w-5 h-5 text-primary" />
                    <span className="text-sm font-medium">Email</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <BellIcon className="w-5 h-5 text-primary" />
                    <span className="text-sm font-medium">PagerDuty & more</span>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Link href="/docs/cli">
                    <Button size="lg" className="cursor-pointer">
                      Install CLI
                      <ArrowRightIcon className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <Link href="/docs/claude-code-skill">
                    <Button size="lg" variant="outline" className="cursor-pointer">
                      Claude Code Skill
                    </Button>
                  </Link>
                </div>
              </div>

              <div>
                <Card className="p-0 overflow-hidden">
                  <div className="bg-muted/50 px-4 py-3 border-b flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span className="text-xs text-muted-foreground ml-2 font-mono">
                      Claude Code
                    </span>
                  </div>
                  <div className="p-6 font-mono text-sm space-y-4">
                    <div>
                      <span className="text-muted-foreground">❯ </span>
                      <span>Monitor my API at api.example.com every 10 seconds</span>
                    </div>
                    <div className="text-muted-foreground pl-4 space-y-1">
                      <p>✓ CLI installed (v0.7.3)</p>
                      <p>✓ Authenticated as team@example.com</p>
                      <p>
                        ✓ Created HTTPS monitor{" "}
                        <span className="text-foreground">&quot;api.example.com&quot;</span>
                      </p>
                      <p className="text-xs">
                        &nbsp; Regions: us-east-1, eu-west-1, ap-northeast-1
                      </p>
                      <p className="text-xs">&nbsp; Interval: 10s · Threshold: 2 · SSL: ✓</p>
                    </div>
                    <div className="border-t pt-4">
                      <span className="text-muted-foreground">❯ </span>
                      <span>Alert me on Slack when it goes down</span>
                    </div>
                    <div className="text-muted-foreground pl-4 space-y-1">
                      <p>
                        ✓ Created Slack contact{" "}
                        <span className="text-foreground">&quot;#ops-alerts&quot;</span>
                      </p>
                      <p>✓ Attached to api.example.com monitor</p>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <Separator />

        {/* Core Value Props */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold mb-4">
                Website & Server Uptime Monitoring for Modern Teams
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Stop losing customers to undetected downtime. Get alerts that actually save revenue.
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              <Card className="p-8 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-6">
                  <ClockIcon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">10-Second Detection</h3>
                <p className="text-muted-foreground">
                  Get alerted in 10 seconds, not 3 minutes. While competitors average 30-180 seconds
                  between checks, we never stop watching.
                </p>
              </Card>

              <Card className="p-8 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-6">
                  <LinkIcon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Works With Your Stack</h3>
                <p className="text-muted-foreground mb-4">
                  PagerDuty, incident.io, Slack, Discord, webhooks, and more. Automate everything
                  with our API and CLI.
                </p>
                <Link
                  href="/docs/notifications"
                  className="text-primary hover:underline text-sm font-medium"
                >
                  View all integrations →
                </Link>
              </Card>

              <Card className="p-8 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-6">
                  <ShieldCheckIcon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Far Fewer False Alarms</h3>
                <p className="text-muted-foreground mb-4">
                  Multi-region verification minimizes false positives. Only get paged for real
                  downtime, not network blips or single-point failures.
                </p>
                <Link
                  href="/docs/best-practices"
                  className="text-primary hover:underline text-sm font-medium"
                >
                  Read best practices →
                </Link>
              </Card>
            </div>
          </div>
        </section>

        <Separator />

        {/* Incident Management Integration */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <Badge variant="outline" className="mb-6">
                  <BellIcon className="w-3 h-3 mr-1" />
                  Incident Response
                </Badge>

                <h2 className="text-3xl font-bold mb-4">Your Tools, Our Detection</h2>

                <p className="text-lg text-muted-foreground mb-6">
                  We detect issues faster than any other service and send rich alerts to your
                  existing incident management tools. Native integrations mean no webhook
                  maintenance or custom scripts.
                </p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <p className="font-medium">Automatic Incident Creation</p>
                      <p className="text-sm text-muted-foreground">
                        Issues flow directly into PagerDuty or Opsgenie with full context
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <p className="font-medium">Respect Your Escalation Policies</p>
                      <p className="text-sm text-muted-foreground">
                        Your on-call schedules and routing rules remain in your control
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <p className="font-medium">Rich Context & Runbooks</p>
                      <p className="text-sm text-muted-foreground">
                        Include response times, affected regions, and custom metadata
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Link href="/docs/notifications/pagerduty">
                    <Button className="cursor-pointer">PagerDuty Setup Guide</Button>
                  </Link>
                  <Link href="/docs/notifications/opsgenie">
                    <Button variant="outline" className="cursor-pointer">
                      Opsgenie Setup Guide
                    </Button>
                  </Link>
                </div>
              </div>

              <div className="space-y-4">
                <Card className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-2 h-2 rounded-full bg-red-600 animate-pulse"></div>
                    <h4 className="font-semibold">Alert Flow Example</h4>
                  </div>

                  <div className="space-y-3 font-mono text-sm">
                    <div className="flex items-center gap-3">
                      <span className="text-muted-foreground">00:00</span>
                      <span>Check from us-east-2: all good</span>
                    </div>
                    <div className="flex items-center gap-3 text-red-600">
                      <span className="text-muted-foreground">00:06</span>
                      <span className="font-semibold">Server crashes</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-muted-foreground">00:10</span>
                      <span>Check from us-east-1: error</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-muted-foreground">00:20</span>
                      <span>Check from eu-west-1: error</span>
                    </div>
                    <div className="flex items-center gap-3 text-red-600">
                      <span className="text-muted-foreground">00:20</span>
                      <span className="font-semibold">Alert triggered</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-muted-foreground">00:20</span>
                      <span>Status page updated automatically</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-muted-foreground">00:21</span>
                      <span>PagerDuty incident #1234 created</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-muted-foreground">00:21</span>
                      <span>
                        On-call engineer paged <span className="font-semibold">in ~15 seconds</span>{" "}
                        after crash
                      </span>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 bg-muted/50">
                  <h4 className="font-semibold mb-3">Supported Integrations</h4>
                  <div className="flex flex-wrap gap-2">
                    {[
                      {
                        name: "incident.io",
                        href: "/docs/notifications/incident-io",
                      },
                      {
                        name: "PagerDuty",
                        href: "/docs/notifications/pagerduty",
                      },
                      {
                        name: "Opsgenie",
                        href: "/docs/notifications/opsgenie",
                      },
                      { name: "Terraform", href: "/docs/terraform" },
                      { name: "Slack", href: "/docs/notifications/slack" },
                      { name: "Discord", href: "/docs/notifications/discord" },
                      {
                        name: "Webhooks",
                        href: "/docs/notifications/webhooks",
                      },
                      { name: "Email", href: "/docs/notifications/email" },
                    ].map((integration) => (
                      <Link key={integration.name} href={integration.href} className="inline-flex">
                        <Badge
                          variant="secondary"
                          className="hover:bg-secondary/80 cursor-pointer transition-colors"
                        >
                          {integration.name}
                        </Badge>
                      </Link>
                    ))}
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Status Pages Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="mb-4">
                <DocumentCheckIcon className="w-3 h-3 mr-1" />
                Included Free
              </Badge>
              <h2 className="text-3xl font-bold mb-4">Professional Status Pages</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Keep your customers informed with public status pages on your own domain.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              <Card className="p-6">
                <h3 className="font-semibold mb-2">Your Domain</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Host at status.yourcompany.com with automatic SSL
                </p>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold mb-2">Real-time Updates</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Automatically reflects the current status of all monitors
                </p>
                <div className="flex gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-600"></div>
                  <div className="w-2 h-2 rounded-full bg-green-600"></div>
                  <div className="w-2 h-2 rounded-full bg-red-600"></div>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold mb-2">Incident Management</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Post updates, track progress, maintain incident history
                </p>
                <Badge variant="outline" className="text-xs">
                  90-day retention
                </Badge>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold mb-2">Public Visibility</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Share your service status transparently with customers
                </p>
                <Badge variant="outline" className="text-xs">
                  Always accessible
                </Badge>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold mb-2">Password Protection</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Optional basic auth for internal status pages
                </p>
                <Badge variant="secondary" className="text-xs">
                  Pro feature
                </Badge>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold mb-2">Multiple Pages</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Create separate status pages for different services
                </p>
                <Badge variant="default" className="text-xs">
                  Unlimited pages
                </Badge>
              </Card>
            </div>

            <div className="text-center">
              <Link
                href="https://status.uptime-monitor.io/6883a5c53de755cc0ebc41e8"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button size="lg" variant="outline">
                  View Live Demo
                  <ArrowRightIcon className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <Separator />

        {/* Pricing */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Transparent Pricing</h2>
              <p className="text-lg text-muted-foreground">
                Start with a 7-day free trial. No credit card required.
              </p>
            </div>

            <div className="max-w-lg mx-auto mb-12">
              <Card className="p-8 border-primary shadow-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xl font-semibold">Pro</h3>
                  <Badge>7-Day Free Trial</Badge>
                </div>
                <div className="text-3xl font-bold mb-1">
                  From $4.95
                  <span className="text-lg font-normal text-muted-foreground">/mo</span>
                </div>
                <p className="text-sm text-muted-foreground mb-6">
                  Plans from 10 to 1,000 monitors.
                </p>

                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-2 text-sm">
                    <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Multi-region verification for each monitor
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <strong>10-second checks - fastest of any managed service</strong>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                    HTTPS, TCP, ping, and SSL certificate monitoring
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Unlimited integration alerts (Slack, webhooks, PagerDuty, etc.)
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Status pages on a custom domain
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                    API access, CLI, and Terraform provider
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Team collaboration with role-based access
                  </li>
                </ul>

                <Link href="/accounts/login">
                  <Button className="w-full cursor-pointer">Start Free Trial</Button>
                </Link>
              </Card>
            </div>

            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-4">
                Need more monitors or custom requirements?
              </p>
              <a href="mailto:billing@uptime-monitor.io?subject=Enterprise%20Pricing%20Inquiry">
                <Button variant="link">Contact us for Enterprise pricing</Button>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
